import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { IconComponent } from '../../../shared/components/icons/icon/icon.component';
import { ModalComponent } from '../../../shared/components/modal/modal/modal.component';
import { DEFAULT_LOCALE } from '../../../shared/i18n/i18n.config';
import { WINDOW_OBJECT } from '../../../shared/injectionTokens';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { setDefaultLocale } from '../../../utils/l10n';

import {
    LOCALE_COUNTRY_STORAGE_KEY,
    LOCALE_OPTIONS,
    LOCALE_ZONES,
    LocaleOption,
    LocaleZone,
    resolveLanguageCode,
    resolveSelectedOption,
} from './locale-options';

interface LocaleZoneGroup {
    key: LocaleZone;
    titleKey: string;
    options: readonly LocaleOption[];
}

@Component({
    selector: 'app-language-selector',
    imports: [ModalComponent, IconComponent, TranslatePipe],
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly translateService = inject(TranslateService);
    private readonly _window = inject<Window>(WINDOW_OBJECT);

    readonly open = input<boolean>(false);
    readonly closed = output<void>();

    protected readonly selectedOption = signal(this.resolveInitialOption());

    /** Locale options grouped by zone, alphabetically by country within each zone. */
    protected readonly zones = computed<LocaleZoneGroup[]>(() =>
        LOCALE_ZONES.map((zone) => ({
            ...zone,
            options: LOCALE_OPTIONS.filter((option) => option.zone === zone.key).sort((a, b) =>
                a.country.localeCompare(b.country),
            ),
        })).filter((zone) => zone.options.length > 0),
    );

    protected isSelected(option: LocaleOption): boolean {
        const selected = this.selectedOption();
        return !!selected && selected.country === option.country && selected.language === option.language;
    }

    protected onLocaleSelected(option: LocaleOption): void {
        // Picking the locale already in use only needs to dismiss the modal; no reload.
        if (this.isSelected(option)) {
            this.closed.emit();
            return;
        }

        this.selectedOption.set(option);
        this.closed.emit();
        this.analyticsService.selectLanguage(option.language);
        this.translateService.use(option.language);
        setDefaultLocale(option.language);
        this.localStorageService.setItem('locale', option.language);
        this.localStorageService.setItem(LOCALE_COUNTRY_STORAGE_KEY, option.country);
        this._window.location.reload();
    }

    private resolveInitialOption(): LocaleOption | undefined {
        const language = resolveLanguageCode(this.translateService.getCurrentLang() || DEFAULT_LOCALE);
        return resolveSelectedOption(language, this.localStorageService.getItem(LOCALE_COUNTRY_STORAGE_KEY));
    }
}
